<template>
  <BaseCard>
    <BaseCardBody>
      <p class="font-bold">
        {{ title }}
      </p>
      <p class="text-sm">
        {{ description }}
      </p>
      <div v-if="complianceAttachments?.length" class="flex flex-row justify-end pt-2">
        <div v-if="complianceAttachments.length === 1">
          <BaseButton
            density="comfortable"
            prepend-icon="mdi-printer"
            @click="printCompliance(selectedAttachment?.id)"
          >
            Préremplir le bon de prise en charge
          </BaseButton>
        </div>
        <div v-else>
          <AttachmentSelectionDropdown
            title="Préremplir le bon de prise en charge"
            :attachments="complianceAttachments"
            @on-click="printCompliance"
          />
        </div>
        <AttachmentPrefillerOptionsModal
          v-if="selectedAttachment && openPrefillerModal"
          v-model:open="openPrefillerModal"
          :attachment="selectedAttachment"
          :consumer="consumer"
          @on-close="openPrefillerModal=false"
        />
      </div>
      <attachment-grid
        :attachments="attachments"
        class="p-5"
        :attachable-id="Number($route.params.programId)"
        attachable-type="programs"
        :consumer="consumer"
      />
    </BaseCardBody>
  </BaseCard>
</template>

<script setup lang="ts">
import {computed, onMounted, PropType, ref} from "vue";
import BaseCard from '@/core/components/base/BaseCard.vue';
import BaseCardBody from '@/core/components/base/BaseCardBody.vue';
import AttachmentGrid from '@/attachment/components/AttachmentGrid.vue';
import {programAttachmentApi} from '@/container';
import {Attachment} from "@/attachment/interfaces/attachment";
import {useRoute} from "vue-router";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {storeToRefs} from "pinia";
import {usePatientStore} from "@/stores/patient.store";
import AttachmentSelectionDropdown from "@/attachment/components/AttachmentSelectionDropdown.vue";
import AttachmentPrefillerOptionsModal from "@/attachment/components/AttachmentPrefillerOptionsModal.vue";
import Beneficiary from "@/customer/interfaces/Beneficiary";
import Customer from "@/customer/interfaces/Customer";

const props = defineProps({
    attachments: {
        type: Array as PropType<Attachment[]>,
        required: true
    },
    title: {
        type: String,
        default: 'Fiches conseils'
    },
    description: {
        type: String,
        default: 'Plusieurs documents informatifs sont à votre disposition ci-dessous.' +
            'Imprimez-les et remettez les à vos patients'
    },
    consumer: {
        type: Object as PropType<Customer | Beneficiary | undefined>,
        default: undefined
    }
});

const complianceAttachments = ref<Attachment[] | null>(null);
const selectedAttachment = ref<Attachment | null>(null);
const openPrefillerModal = ref<boolean>(false);
const route = useRoute();
const {customer} = storeToRefs(usePatientStore());

const consumerKey = computed(() => props.consumer?.consumer_key || customer.value?.consumer_key);

onMounted(() => {
    fetchComplianceAttachment();
});

/**
 * Get compliance attachment of the program if there is any.
 */
const fetchComplianceAttachment = () => {
    if (route.params.programId) {
        programAttachmentApi().programIndex(Number(route.params.programId), {tags: ['compliance']})
            .then(response => {
                if (response?.length) {
                    complianceAttachments.value = response;
                    if (response.length >= 1) {
                        selectedAttachment.value = response[0];
                    }
                }
            });
    }
};

/**
 * Open the prefiller modal for the chosen compliance attachment.
 * The modal is only mounted while open, so it always starts from the options of that attachment.
 */
const printCompliance = (attachmentId?: number) => {
    if (attachmentId && consumerKey.value) {
        selectedAttachment.value = complianceAttachments.value?.find(attachment => attachment.id === attachmentId) ?? null;
        openPrefillerModal.value = true;
    }
};

</script>
